import React, { useState, useEffect } from "react";
import Button from "../../../components/common/Button";
import Badge from "../../../components/common/Badge";
import {
  TagIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  FolderIcon,
  FolderOpenIcon,
  CollectionIcon,
  ChartBarIcon,
  EyeIcon,
  ShoppingBagIcon,
  PencilIcon,
  TrashIcon,
  RefreshIcon,
  ExternalLinkIcon,
  DotsVerticalIcon,
  HomeIcon,
  DeviceMobileIcon,
  MenuIcon,
  StarIcon,
  ArrowUpIcon,
} from "@heroicons/react/outline";

const CategoryCard = ({
  category,
  handleSingleAction,
  expandedByDefault = false,
  allowToggle = true,
  showStats = true,
  variant = "list", // "list" | "grid" | "compact"
}) => {
  const [isExpanded, setIsExpanded] = useState(
    expandedByDefault || !allowToggle
  );
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    setIsExpanded(expandedByDefault || !allowToggle);
  }, [expandedByDefault, allowToggle]);

  const toggleExpanded = () => {
    if (allowToggle) {
      setIsExpanded(!isExpanded);
    }
  };

  // Status helpers
  const isActive = category.display?.isActive;
  const isFeatured = category.display?.isFeatured;
  const showInMenu = category.display?.showInMenu;
  const showInHomepage = category.display?.showInHomepage;
  const showInMobile = category.display?.showInMobile;
  const productCount =
    category.productCount || category.stats?.totalProducts || 0;
  const hasParent = !!category.parentId;

  // Build parent chain display
  const getParentDisplay = () => {
    if (!category.parentId) return null;
    return category.parentId.name || "Parent Category";
  };

  // Action handlers with event stopping
  const handleAction = (action) => (e) => {
    e?.stopPropagation();
    setShowMobileMenu(false);
    handleSingleAction(category._id, action);
  };

  // Grid variant - compact card for grid view
  if (variant === "grid") {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden h-full flex flex-col">
        {/* Card Header */}
        <div className="p-4 flex-1">
          {/* Status Row */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1.5">
              {isActive ? (
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              ) : (
                <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
              )}
              <span
                className={`text-xs font-medium ${
                  isActive ? "text-green-600" : "text-gray-400"
                }`}
              >
                {isActive ? "Active" : "Inactive"}
              </span>
            </div>

            {isFeatured && (
              <Badge variant="primary" className="text-xs">
                <StarIcon className="w-3 h-3 mr-1" />
                Featured
              </Badge>
            )}
          </div>

          {/* Category Icon & Name */}
          <div className="flex items-start gap-3 mb-3">
            <div
              className={`p-2.5 rounded-lg flex-shrink-0 ${
                isActive ? "bg-primary/10" : "bg-gray-100"
              }`}
            >
              <FolderIcon
                className={`w-5 h-5 ${
                  isActive ? "text-primary" : "text-gray-400"
                }`}
              />
            </div>
            <div className="min-w-0 flex-1">
              <h3
                className={`font-semibold text-base truncate ${
                  !isActive && "text-gray-400"
                }`}
              >
                {category.name}
              </h3>
              <p className="text-xs text-gray-500 font-mono mt-0.5">
                {category.categoryId || `#${category._id?.slice(-6)}`}
              </p>
            </div>
          </div>

          {/* Parent Category */}
          {hasParent && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3 bg-gray-50 px-2 py-1.5 rounded-md">
              <ChevronRightIcon className="w-3 h-3" />
              <span className="truncate">{getParentDisplay()}</span>
            </div>
          )}

          {/* Quick Stats */}
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <ShoppingBagIcon className="w-3.5 h-3.5" />
              <span>{productCount}</span>
            </div>
            <div className="flex items-center gap-1">
              <span>Level {category.parentLevel || 0}</span>
            </div>
          </div>

          {/* Visibility Icons */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <div
              className={`p-1 rounded ${
                showInMenu
                  ? "bg-blue-50 text-blue-500"
                  : "bg-gray-50 text-gray-300"
              }`}
              title="Menu"
            >
              <MenuIcon className="w-3.5 h-3.5" />
            </div>
            <div
              className={`p-1 rounded ${
                showInMobile
                  ? "bg-green-50 text-green-500"
                  : "bg-gray-50 text-gray-300"
              }`}
              title="Mobile"
            >
              <DeviceMobileIcon className="w-3.5 h-3.5" />
            </div>
            <div
              className={`p-1 rounded ${
                showInHomepage
                  ? "bg-purple-50 text-purple-500"
                  : "bg-gray-50 text-gray-300"
              }`}
              title="Homepage"
            >
              <HomeIcon className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>

        {/* Card Actions */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex gap-2">
          <Button
            variant="primary-outline"
            size="sm"
            onClick={handleAction("edit")}
            className="flex-1 text-xs"
          >
            <PencilIcon className="w-3.5 h-3.5 mr-1" />
            Edit
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleAction("view")}
            className="flex-1 text-xs"
          >
            <EyeIcon className="w-3.5 h-3.5 mr-1" />
            View
          </Button>
          <Button
            variant="danger-text"
            size="sm"
            onClick={handleAction("delete")}
            className="!px-2"
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // List variant (default) - full width expandable card
  return (
    <div
      className={`bg-white border rounded-xl shadow-sm transition-all duration-200 overflow-hidden ${
        isExpanded
          ? "border-primary/30 shadow-md"
          : "border-gray-200 hover:border-gray-300"
      }`}
    >
      {/* Card Header */}
      <div
        className={`${
          allowToggle ? "cursor-pointer" : ""
        } transition-colors duration-200 ${
          isExpanded ? "bg-gray-50/50" : "hover:bg-gray-50/30"
        }`}
        onClick={toggleExpanded}
        role={allowToggle ? "button" : undefined}
        aria-expanded={allowToggle ? isExpanded : undefined}
      >
        <div className="p-4 sm:p-5">
          {/* Mobile Layout */}
          <div className="flex flex-col gap-4 sm:hidden">
            {/* Top Row - Icon, Name, Status */}
            <div className="flex items-start gap-3">
              <div
                className={`p-2.5 rounded-lg flex-shrink-0 ${
                  isActive ? "bg-primary/10" : "bg-gray-100"
                }`}
              >
                {isExpanded ? (
                  <FolderOpenIcon
                    className={`w-5 h-5 ${
                      isActive ? "text-primary" : "text-gray-400"
                    }`}
                  />
                ) : (
                  <FolderIcon
                    className={`w-5 h-5 ${
                      isActive ? "text-primary" : "text-gray-400"
                    }`}
                  />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2
                    className={`text-base font-semibold ${
                      !isActive && "text-gray-400"
                    }`}
                  >
                    {category.name}
                  </h2>
                  <div className="flex gap-1">
                    {isActive ? (
                      <Badge variant="success" className="text-xs">
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="danger" className="text-xs">
                        Inactive
                      </Badge>
                    )}
                    {isFeatured && (
                      <Badge variant="primary" className="text-xs">
                        Featured
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                  <span className="font-mono">
                    {category.categoryId || `#${category._id?.slice(-6)}`}
                  </span>
                  {hasParent && (
                    <>
                      <span>•</span>
                      <span className="flex items-center gap-1 truncate">
                        <FolderIcon className="w-3 h-3" />
                        {getParentDisplay()}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Mobile Menu Toggle */}
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <DotsVerticalIcon className="w-5 h-5 text-gray-500" />
                </button>

                {/* Mobile Dropdown Menu */}
                {showMobileMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowMobileMenu(false)}
                    />
                    <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[140px]">
                      <button
                        onClick={handleAction("edit")}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        <PencilIcon className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={handleAction("view")}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        <EyeIcon className="w-4 h-4" />
                        View Details
                      </button>
                      <button
                        onClick={handleAction("syncStats")}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        <RefreshIcon className="w-4 h-4" />
                        Sync Stats
                      </button>
                      {hasParent && (
                        <button
                          onClick={handleAction("moveToRoot")}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        >
                          <ArrowUpIcon className="w-4 h-4" />
                          Move to Root
                        </button>
                      )}
                      <hr className="my-1" />
                      <button
                        onClick={handleAction("delete")}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                      >
                        <TrashIcon className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Stats Row */}
            {showStats && (
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1.5 bg-gray-100 px-2 py-1 rounded-md">
                  <ShoppingBagIcon className="w-3.5 h-3.5" />
                  <span>{productCount} products</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span>Level {category.parentLevel || 0}</span>
                </div>
                {category.stats?.totalViews > 0 && (
                  <div className="flex items-center gap-1.5">
                    <EyeIcon className="w-3.5 h-3.5" />
                    <span>{category.stats.totalViews}</span>
                  </div>
                )}
              </div>
            )}

            {/* Expand Indicator */}
            {allowToggle && (
              <div className="flex justify-center -mb-2">
                <ChevronDownIcon
                  className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${
                    isExpanded ? "rotate-180" : ""
                  }`}
                />
              </div>
            )}
          </div>

          {/* Desktop Layout */}
          <div className="hidden sm:flex sm:items-center sm:justify-between gap-4">
            {/* Left - Category Info */}
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div
                className={`p-3 rounded-xl flex-shrink-0 ${
                  isActive ? "bg-primary/10" : "bg-gray-100"
                }`}
              >
                {isExpanded ? (
                  <FolderOpenIcon
                    className={`w-6 h-6 ${
                      isActive ? "text-primary" : "text-gray-400"
                    }`}
                  />
                ) : (
                  <FolderIcon
                    className={`w-6 h-6 ${
                      isActive ? "text-primary" : "text-gray-400"
                    }`}
                  />
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2
                    className={`text-lg font-semibold ${
                      !isActive && "text-gray-400"
                    }`}
                  >
                    {category.name}
                  </h2>
                  <div className="flex gap-1.5">
                    {isActive ? (
                      <Badge variant="success" className="text-xs">
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="danger" className="text-xs">
                        Inactive
                      </Badge>
                    )}
                    {isFeatured && (
                      <Badge variant="primary" className="text-xs">
                        <StarIcon className="w-3 h-3 mr-0.5" />
                        Featured
                      </Badge>
                    )}
                    {showInMenu && (
                      <Badge variant="light" className="text-xs">
                        Menu
                      </Badge>
                    )}
                    {showInHomepage && (
                      <Badge variant="info" className="text-xs">
                        Homepage
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-1.5 text-sm text-gray-500">
                  <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                    {category.categoryId || category._id?.slice(-6)}
                  </span>

                  {hasParent && (
                    <span className="flex items-center gap-1.5">
                      <ChevronRightIcon className="w-3 h-3" />
                      <FolderIcon className="w-3.5 h-3.5" />
                      {getParentDisplay()}
                    </span>
                  )}

                  <span className="text-gray-400">•</span>
                  <span>Level {category.parentLevel || 0}</span>
                </div>
              </div>
            </div>

            {/* Center - Quick Stats */}
            {showStats && (
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-lg">
                  <ShoppingBagIcon className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">{productCount}</span>
                  <span className="text-gray-400 text-xs">products</span>
                </div>

                {category.stats?.totalViews > 0 && (
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <EyeIcon className="w-4 h-4" />
                    <span>{category.stats.totalViews}</span>
                  </div>
                )}
              </div>
            )}

            {/* Right - Actions */}
            <div
              className="flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="secondary"
                size="sm"
                onClick={handleAction("view")}
                className="hidden lg:flex"
              >
                <EyeIcon className="w-4 h-4 mr-1.5" />
                View
              </Button>
              <Button
                variant="primary-outline"
                size="sm"
                onClick={handleAction("edit")}
              >
                <PencilIcon className="w-4 h-4 mr-1.5" />
                Edit
              </Button>
              <Button
                variant="danger-text"
                size="sm"
                onClick={handleAction("delete")}
              >
                <TrashIcon className="w-4 h-4" />
              </Button>

              {allowToggle && (
                <div className="ml-2 pl-2 border-l border-gray-200">
                  <ChevronDownIcon
                    className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isExpanded ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 pb-4 sm:px-5 sm:pb-5 pt-0">
          <div className="border-t border-gray-200 pt-4">
            {/* Responsive Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Basic Information */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <InformationCircleIcon className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold text-sm text-gray-800">
                    Basic Info
                  </h3>
                </div>

                <div className="space-y-2.5 text-sm">
                  {category.slug && (
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-400 uppercase tracking-wide">
                        Slug
                      </span>
                      <span className="font-mono text-gray-700 break-all">
                        {category.slug}
                      </span>
                    </div>
                  )}

                  {category.description && (
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-400 uppercase tracking-wide">
                        Description
                      </span>
                      <p className="text-gray-700 line-clamp-2">
                        {category.description}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-col">
                    <span className="text-xs text-gray-400 uppercase tracking-wide">
                      Display Order
                    </span>
                    <span className="text-gray-700">{category.order || 0}</span>
                  </div>

                  {category.tags && category.tags.length > 0 && (
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                        Tags
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {category.tags.slice(0, 5).map((tag, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-700"
                          >
                            <TagIcon className="w-3 h-3 mr-1" />
                            {tag}
                          </span>
                        ))}
                        {category.tags.length > 5 && (
                          <span className="text-xs text-gray-400">
                            +{category.tags.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Display Settings */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CollectionIcon className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold text-sm text-gray-800">
                    Display Settings
                  </h3>
                </div>

                <div className="space-y-2.5 text-sm">
                  {category.classification?.targetGender && (
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-400 uppercase tracking-wide">
                        Target Audience
                      </span>
                      <span className="text-gray-700">
                        {category.classification.targetGender}
                      </span>
                    </div>
                  )}

                  {category.display?.displayMode && (
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-400 uppercase tracking-wide">
                        Display Mode
                      </span>
                      <span className="text-gray-700">
                        {category.display.displayMode}
                      </span>
                    </div>
                  )}

                  <div className="flex flex-col">
                    <span className="text-xs text-gray-400 uppercase tracking-wide mb-1.5">
                      Visibility
                    </span>
                    <div className="grid grid-cols-2 gap-2">
                      <div
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs ${
                          showInMenu
                            ? "bg-blue-50 text-blue-700"
                            : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        <MenuIcon className="w-3.5 h-3.5" />
                        <span>Menu</span>
                      </div>
                      <div
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs ${
                          showInMobile
                            ? "bg-green-50 text-green-700"
                            : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        <DeviceMobileIcon className="w-3.5 h-3.5" />
                        <span>Mobile</span>
                      </div>
                      <div
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs ${
                          showInHomepage
                            ? "bg-purple-50 text-purple-700"
                            : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        <HomeIcon className="w-3.5 h-3.5" />
                        <span>Homepage</span>
                      </div>
                      <div
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs ${
                          isFeatured
                            ? "bg-amber-50 text-amber-700"
                            : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        <StarIcon className="w-3.5 h-3.5" />
                        <span>Featured</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Statistics */}
              <div className="bg-gray-50 rounded-xl p-4 sm:col-span-2 lg:col-span-1">
                <div className="flex items-center gap-2 mb-3">
                  <ChartBarIcon className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold text-sm text-gray-800">
                    Statistics
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-800">
                      {productCount}
                    </div>
                    <div className="text-xs text-gray-500">Total Products</div>
                  </div>

                  <div className="bg-white rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {category.stats?.activeProducts ||
                        category.activeProductCount ||
                        0}
                    </div>
                    <div className="text-xs text-gray-500">Active</div>
                  </div>

                  {category.stats?.totalViews !== undefined && (
                    <div className="bg-white rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {category.stats.totalViews}
                      </div>
                      <div className="text-xs text-gray-500">Views</div>
                    </div>
                  )}

                  {category.availability && (
                    <div className="bg-white rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {category.availability.allBranches
                          ? "All"
                          : category.availability.specificBranches?.length || 0}
                      </div>
                      <div className="text-xs text-gray-500">Branches</div>
                    </div>
                  )}
                </div>

                {category.stats?.lastUpdated && (
                  <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500 text-center">
                    Last updated:{" "}
                    {new Date(category.stats.lastUpdated).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>

            {/* Actions Footer */}
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleAction("view")}
                className="flex-1 sm:flex-none"
              >
                <ExternalLinkIcon className="w-4 h-4 mr-1.5" />
                View Details
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleAction("syncStats")}
                className="flex-1 sm:flex-none"
              >
                <RefreshIcon className="w-4 h-4 mr-1.5" />
                Sync Stats
              </Button>
              {hasParent && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleAction("moveToRoot")}
                  className="flex-1 sm:flex-none"
                >
                  <ArrowUpIcon className="w-4 h-4 mr-1.5" />
                  Move to Root
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={handleAction("add")}
                className="flex-1 sm:flex-none"
              >
                <FolderIcon className="w-4 h-4 mr-1.5" />
                Add Subcategory
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryCard;
